import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface HFLabel {
  en: string;
  ko: string;
  bn: string;
}

export interface HFLink {
  id: string;
  label: HFLabel;
  href: string;
  is_external: boolean;
}

export interface HFFooterColumn {
  id: string;
  title: HFLabel;
  links: HFLink[];
}

export interface HFSocialLink {
  id: string;
  platform: string;
  url: string;
}

// Same safety-net principle as useSiteImage: the admin-editable tables
// (rxk_nav_links / rxk_footer_columns / rxk_footer_links / rxk_social_links)
// are the source of truth once populated, but a failed fetch, an
// unreachable DB, or an emptied table must never take the header/footer
// down — these are exactly what was hardcoded before link management
// existed, kept only as the last-resort fallback.
const FALLBACK_NAV_LINKS: HFLink[] = [
  { id: 'fallback-brand-story', label: { en: 'Brand Story', ko: '브랜드 스토리', bn: 'ব্র্যান্ড স্টোরি' }, href: '/brand-story', is_external: false },
  { id: 'fallback-three-step', label: { en: '3-Step Treatment', ko: '3-Step 트리트먼트', bn: '3-Step ট্রিটমেন্ট' }, href: '/3-step-treatment', is_external: false },
  { id: 'fallback-products', label: { en: 'Products', ko: '제품', bn: 'প্রোডাক্টস' }, href: '/products', is_external: false },
  { id: 'fallback-content', label: { en: 'Content', ko: '콘텐츠', bn: 'কন্টেন্ট' }, href: '/content', is_external: false },
  { id: 'fallback-where-to-buy', label: { en: 'Where to Buy', ko: '구매처', bn: 'কোথায় পাবেন' }, href: '/stores', is_external: false },
];

const FALLBACK_FOOTER_COLUMNS: HFFooterColumn[] = [
  {
    id: 'fallback-col-brand',
    title: { en: 'Brand', ko: '브랜드', bn: 'ব্র্যান্ড' },
    links: [
      { id: 'fallback-brand-1', label: { en: 'Brand Story', ko: '브랜드 스토리', bn: 'ব্র্যান্ড স্টোরি' }, href: '/brand-story', is_external: false },
      { id: 'fallback-brand-2', label: { en: '3-Step Treatment', ko: '3-Step 트리트먼트', bn: '3-Step ট্রিটমেন্ট' }, href: '/3-step-treatment', is_external: false },
      { id: 'fallback-brand-3', label: { en: 'Our Philosophy', ko: '브랜드 철학', bn: 'আমাদের ফিলোসফি' }, href: '/brand-story', is_external: false },
      { id: 'fallback-brand-4', label: { en: 'Ingredient Research', ko: '성분 연구', bn: 'উপাদান গবেষণা' }, href: '/content', is_external: false },
    ],
  },
  {
    id: 'fallback-col-products',
    title: { en: 'Products', ko: '제품', bn: 'প্রোডাক্টস' },
    links: [
      { id: 'fallback-prod-1', label: { en: 'Step 01 — Skin Priming Booster Essence', ko: 'Step 01 — 스킨 프라이밍 부스터 에센스', bn: 'Step 01 — Skin Priming Booster Essence' }, href: '/products/skin-priming-booster-essence', is_external: false },
      { id: 'fallback-prod-2', label: { en: 'Step 02 — Daily Rescue Serum', ko: 'Step 02 — 데일리 레스큐 세럼', bn: 'Step 02 — Daily Rescue Serum' }, href: '/products/daily-rescue-serum', is_external: false },
      { id: 'fallback-prod-3', label: { en: 'Step 03 — Barrier Seal Cream', ko: 'Step 03 — 베리어 실 크림', bn: 'Step 03 — Barrier Seal Cream' }, href: '/products/barrier-seal-cream', is_external: false },
      { id: 'fallback-prod-4', label: { en: '3-Step Set', ko: '3-Step 세트', bn: '3-Step Set' }, href: '/products', is_external: false },
      { id: 'fallback-prod-5', label: { en: 'Shop All', ko: '전체 제품 보기', bn: 'সব প্রোডাক্ট কিনুন' }, href: '/products', is_external: false },
    ],
  },
  {
    id: 'fallback-col-support',
    title: { en: 'Support', ko: '고객지원', bn: 'সাপোর্ট' },
    links: [
      { id: 'fallback-sup-1', label: { en: 'About Us', ko: '회사 소개', bn: 'আমাদের সম্পর্কে' }, href: '/about', is_external: false },
      { id: 'fallback-sup-2', label: { en: 'Contact Us', ko: '문의하기', bn: 'যোগাযোগ করুন' }, href: '/contact', is_external: false },
      { id: 'fallback-sup-3', label: { en: 'FAQ', ko: '자주 묻는 질문', bn: 'FAQ' }, href: '/faq', is_external: false },
      { id: 'fallback-sup-4', label: { en: 'Shipping & Returns', ko: '배송 및 반품', bn: 'শিপিং এবং রিটার্নস' }, href: '/shipping-returns', is_external: false },
      { id: 'fallback-sup-5', label: { en: 'Privacy Policy', ko: '개인정보 처리방침', bn: 'প্রাইভেসি পলিসি' }, href: '/privacy-policy', is_external: false },
      { id: 'fallback-sup-6', label: { en: 'Terms of Service', ko: '이용약관', bn: 'টার্মস অফ সার্ভিস' }, href: '/terms-of-service', is_external: false },
    ],
  },
];

const FALLBACK_SOCIAL_LINKS: HFSocialLink[] = [
  { id: 'fallback-instagram', platform: 'instagram', url: 'https://www.instagram.com/thekseoul_official/' },
  { id: 'fallback-facebook', platform: 'facebook', url: 'https://www.facebook.com/thekseoulbd' },
];

let navCache: HFLink[] | null = null;
let navInFlight: Promise<HFLink[]> | null = null;

async function fetchNavLinks(): Promise<HFLink[]> {
  try {
    const { data } = await supabase.from('rxk_nav_links').select('id, label, href, is_external').order('sort_order');
    const rows = (data ?? []) as HFLink[];
    navCache = rows.length > 0 ? rows : FALLBACK_NAV_LINKS;
    return navCache;
  } catch {
    return FALLBACK_NAV_LINKS;
  }
}

function loadNavLinks(): Promise<HFLink[]> {
  if (navCache) return Promise.resolve(navCache);
  if (!navInFlight) navInFlight = fetchNavLinks();
  return navInFlight;
}

export function useNavLinks(): HFLink[] {
  const [links, setLinks] = useState<HFLink[]>(navCache ?? FALLBACK_NAV_LINKS);
  useEffect(() => {
    let mounted = true;
    loadNavLinks().then((rows) => {
      if (mounted) setLinks(rows);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return links;
}

interface FooterData {
  columns: HFFooterColumn[];
  social: HFSocialLink[];
}

let footerCache: FooterData | null = null;
let footerInFlight: Promise<FooterData> | null = null;

async function fetchFooterData(): Promise<FooterData> {
  try {
    const [colRes, linkRes, socialRes] = await Promise.all([
      supabase.from('rxk_footer_columns').select('id, title, sort_order').order('sort_order'),
      supabase.from('rxk_footer_links').select('id, column_id, label, href, is_external, sort_order').order('sort_order'),
      supabase.from('rxk_social_links').select('id, platform, url').order('sort_order'),
    ]);
    const columnRows = (colRes.data ?? []) as { id: string; title: HFLabel }[];
    const linkRows = (linkRes.data ?? []) as { id: string; column_id: string; label: HFLabel; href: string; is_external: boolean }[];
    const columns: HFFooterColumn[] = columnRows.map((c) => ({
      id: c.id,
      title: c.title,
      links: linkRows.filter((l) => l.column_id === c.id).map((l) => ({ id: l.id, label: l.label, href: l.href, is_external: l.is_external })),
    }));
    const social = (socialRes.data ?? []) as HFSocialLink[];
    footerCache = {
      columns: columns.length > 0 ? columns : FALLBACK_FOOTER_COLUMNS,
      social: social.length > 0 ? social : FALLBACK_SOCIAL_LINKS,
    };
    return footerCache;
  } catch {
    return { columns: FALLBACK_FOOTER_COLUMNS, social: FALLBACK_SOCIAL_LINKS };
  }
}

function loadFooterData(): Promise<FooterData> {
  if (footerCache) return Promise.resolve(footerCache);
  if (!footerInFlight) footerInFlight = fetchFooterData();
  return footerInFlight;
}

export function useFooterData(): FooterData {
  const [data, setData] = useState<FooterData>(footerCache ?? { columns: FALLBACK_FOOTER_COLUMNS, social: FALLBACK_SOCIAL_LINKS });
  useEffect(() => {
    let mounted = true;
    loadFooterData().then((d) => {
      if (mounted) setData(d);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return data;
}
